/**
 * contactEngineConfig.tsx — config silnika formatek dla kartoteki Kontakty
 * kontrahenta (keystone unifikacja-formatek — mirror `contractorEngineConfig.tsx` /
 * `companyEngineConfig.tsx` / `userEngineConfig.tsx`).
 *
 * WSZYSTKIE pola core kontaktu dostają custom renderer — zero natywnej delegacji do
 * `ExtFieldValueInput` (mirror kontrahenta/firmy/użytkownika), więc gałąź natywna silnika jest
 * martwa dla tej kartoteki — `coerceEmptyToNull` zostaje kosmetyczne.
 *
 * Etykiety: `ctx.field.name` (definicja pola z `GET /system-modules/contractor_contacts/fields`,
 * DB-driven), NIE i18n — zachowanie 1:1 z `contactCoreRenderers.tsx` (przed migracją, plik
 * usunięty razem z rejestrem `CONTACT_CORE_RENDERERS`, silnik go zastępuje).
 *
 * Brak kanału `extra` (`KartotekaEngineConfig<ContactFormData>`, bez drugiego parametru) — żaden
 * renderer kontaktu nie potrzebuje stanu/callbacków runtime specyficznych dla strony (mirror
 * `letterEngineConfig.ts`, jedyny inny config bez `TExtra`; kontrahent/firma/użytkownik go mają).
 *
 * Renderery pól tekstowych mają `invalid` (czerwona ramka) ORAZ `error={ctx.getError?.(...)}`
 * (tekst błędu pod polem) — ujednolicenie walidacji 2026-09. `ContractorContactDetail.tsx`
 * przekazuje do runtime `hasError`/`getError`/`clearError`, tak jak kontrahent/firma.
 *
 * ── WRINKLE 1 (raw → primitive, §4.12) ──────────────────────────────────────────────────
 * `is_primary`/`is_decision_maker` migrują z gołego `<input type="checkbox">` na `<Checkbox>`;
 * `notes` z gołego `<textarea>` na `<Textarea>` — jedyne 3 surowe elementy z
 * `contactCoreRenderers.tsx` (baseline `eslint-suppressions.json` miał `count: 3` dla tego pliku;
 * usunięty razem z plikiem, NIE odtworzony dla tego configu — dług faktycznie znika, nie
 * przenosi się). Business rule „odznacz poprzedni primary" zostaje po stronie serwera
 * (`PATCH/POST .../contacts`) — front tylko wysyła boolean, bez zmian.
 *
 * ── WRINKLE 2 (gdpr_consent_at — PRE-EXISTING gap, REPLIKOWANY, NIE naprawiany) ──────────
 * Slug istnieje w `ContactFormData`/payloadzie API (`gdprConsentAt`), ale NIGDY nie miał
 * renderera w `CONTACT_CORE_RENDERERS` (w przeciwieństwie do Kontrahentów, którzy go renderują).
 * Co więcej — `gdpr_consent_at` w ogóle NIE jest w katalogu `coreFields` modułu
 * `contractor_contacts` (`apps/api/lib/seed-system-modules.ts`; tylko `contractors` go ma), więc
 * builder nigdy nie mógł dodać go do layoutu — dziś silnik metadanych (`/fields`) go nie zwraca,
 * `renderField('gdpr_consent_at')` nie znalazłby pola i zwrócił `null` zanim dojdzie do configu.
 * `fields.gdpr_consent_at` MIMO TO dostaje `structuralNull: true` (bez renderera) — defensywnie,
 * gdyby kiedyś slug trafił do katalogu/warstwy metadanych — żeby odtworzyć DOKŁADNIE dzisiejszy
 * efekt (pole nigdy się nie renderuje) BEZ wpadania w bursztynowy fallback silnika „unsupported
 * field" (patrz `useKartotekaRenderField.tsx` — core-slug spoza `config.fields` = amber warning).
 * Zostaje w `CONTACT_CORE_FIELD_SLUGS` (dla `buildContactCoreRecordValues`/warunków widoczności)
 * — TYLKO bez renderowalnego widgetu. Świadomie NIE naprawiamy (osobna decyzja produktowa).
 *
 * ── TRZECI gap (znaleziony przy tej migracji, analogiczny do Wrinkle 2, NIEwspomniany w
 * briefie — REPLIKOWANY z tego samego powodu) ────────────────────────────────────────────
 * `is_active` JEST w katalogu `coreFields` modułu `contractor_contacts` (real core field,
 * `isCore: true`, więc TEORETYCZNIE dostępny w builderze), ale nie ma odpowiednika w
 * `ContactFormData`/`CONTACT_CORE_RENDERERS`/`CONTACT_CORE_FIELD_SLUGS` — dziś, jeśli ktoś
 * dodałby go do layoutu, `ContactDataTab` (przed migracją) po prostu zwracał `null`
 * (`hasContactCoreRenderer('is_active')` === false). Bez configu w silniku toby wpadło w
 * bursztynowy fallback „unsupported field" (REGRESJA — dziś cicho nic, po migracji brzydki
 * warning box) — `fields.is_active` dostaje `structuralNull: true` (bez `stateKey`, bo nie ma
 * odpowiednika w `ContactFormData`) żeby zachować dzisiejsze zachowanie 1:1.
 */

import { CONTACT_PREFERRED_CHANNELS, RECORD_LANGUAGES } from '@saas/shared';

import { Checkbox } from '@/components/Checkbox';
import { CoreInput, type CoreInputProps } from '@/components/CoreInput';
import { CORE_INPUT_CLASS } from '@/components/coreRendererClasses';
import { Dropdown } from '@/components/Dropdown';
import { GroupsInput } from '@/components/groups/GroupsInput';
import { Field } from '@/components/kartoteki/CoreFieldLayout';
import type {
  CoreFieldConfig,
  FieldRenderContext,
  FieldRenderer,
  KartotekaEngineConfig,
} from '@/components/kartoteki/engine/engine-config';
import { RecordCategoryPicker } from '@/components/record-categories/RecordCategoryPicker';
import { Textarea } from '@/components/Textarea';
import { contactPreferredChannelLabel, recordLanguageLabel } from '@/lib/core-field-option-labels';
import { cn } from '@/lib/utils';

import { CONTACT_CORE_FIELD_SLUGS, type ContactFormData } from './ContactDataTab';

type Ctx = FieldRenderContext<ContactFormData>;

/** Ramka błędu — parytet z `CoreInput`'s `INVALID_CLASS` (patrz CoreInput.tsx). */
const INVALID_CLASS = 'border-red-500 ring-1 ring-red-500';

function TextInput(props: Omit<CoreInputProps, 'variant'>) {
  return <CoreInput {...props} />;
}

function LanguageSelect({ ctx }: { ctx: Ctx }) {
  return (
    <Dropdown
      value={ctx.form.language ?? ''}
      onChange={(v) => ctx.setField({ language: (v || null) as ContactFormData['language'] })}
      options={[
        { value: '', label: '—' },
        ...RECORD_LANGUAGES.map((v) => ({ value: v, label: recordLanguageLabel(v, ctx.t) })),
      ]}
      disabled={!ctx.editable}
      data-testid="contact-field-language"
      className={cn(CORE_INPUT_CLASS, ctx.hasError?.('language') && INVALID_CLASS)}
    />
  );
}

function PreferredChannelSelect({ ctx }: { ctx: Ctx }) {
  return (
    <Dropdown
      value={ctx.form.preferredChannel ?? ''}
      onChange={(v) => ctx.setField({ preferredChannel: v || null })}
      options={[
        { value: '', label: ctx.t('common.notSet') },
        ...CONTACT_PREFERRED_CHANNELS.map((v) => ({
          value: v,
          label: contactPreferredChannelLabel(v, ctx.t),
        })),
      ]}
      disabled={!ctx.editable}
      data-testid="contact-field-preferredChannel"
      className={cn(CORE_INPUT_CLASS, ctx.hasError?.('preferredChannel') && INVALID_CLASS)}
    />
  );
}

// ─── Ground-truth fieldType per slug (z seeda, apps/api/lib/seed-system-modules.ts
// → contractor_contacts.coreFields) — używany WYŁĄCZNIE przez fallback statyczny silnika
// (403 na metadanych modułu). Renderer i tak owns widget. `is_active` dopisane mimo braku
// renderera (patrz nagłówek pliku, trzeci gap) — fallback statyczny i tak nie ma znaczenia
// dla structuralNull (renderField zwraca null zanim dojdzie do fieldType), ale trzymamy
// wpis dla kompletności ground-truth listy. ───────────────────────────────────────────
const CONTACT_FIELD_TYPES: Record<string, string> = {
  groups: 'multiselect',
  category_id: 'select',
  title: 'varchar',
  first_name: 'varchar',
  last_name: 'varchar',
  position: 'varchar',
  department: 'varchar',
  email: 'email',
  phone: 'varchar',
  mobile: 'varchar',
  preferred_channel: 'select',
  language: 'select',
  birthday: 'date',
  // Serwer NIE waliduje formatu (tylko max 255) — 'varchar', żeby wpis bez schematu nie blokował
  // zapisu. Wizualnie input i tak jest `type="url"`.
  linkedin_url: 'varchar',
  is_primary: 'boolean',
  is_decision_maker: 'boolean',
  is_active: 'boolean',
  gdpr_consent_at: 'date',
  notes: 'text',
};

// ─── Mapa renderers (slug → FieldRenderer), portowane 1:1 z contactCoreRenderers.tsx ────

/**
 * Grupy ze słownika `contractor_contact_groups` — wielokrotny wybór. Renderer TUTAJ, żeby o
 * miejscu pola na formatce decydował LAYOUT z buildera (wzorzec 1:1 z pozostałymi kartotekami).
 */
const groupsRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name} colSpan={2}>
    <GroupsInput
      dictionaryCode="contractor_contact_groups"
      value={ctx.form.groups}
      onChange={(next) => ctx.setField({ groups: next })}
      disabled={!ctx.editable}
      data-testid="contact-groups"
    />
  </Field>
);

/**
 * Kategoria z realnej tabeli `record_categories` (kartoteka_key='contractor_contacts') —
 * pojedynczy wybór, NOWA oś (migracja 0127; zastąpiła stary słownik contact_categories).
 * `RecordCategoryPicker` rysuje własny `<Field>`, więc renderer nie owija go drugi raz.
 */
const categoryRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <RecordCategoryPicker
    kartotekaKey="contractor_contacts"
    value={ctx.form.categoryId}
    onChange={(next) => ctx.setField({ categoryId: next })}
    editable={ctx.editable}
    testId="contact-category"
  />
);

const titleRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('title')}>
    <TextInput
      testId="contact-field-title"
      value={ctx.form.title ?? ''}
      onChange={(v) => ctx.setField({ title: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('title')}
    />
  </Field>
);

const firstNameRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('firstName')}>
    <TextInput
      testId="contact-field-firstName"
      value={ctx.form.firstName ?? ''}
      onChange={(v) => ctx.setField({ firstName: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('firstName')}
    />
  </Field>
);

const lastNameRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name} required={ctx.field.isRequired} error={ctx.getError?.('lastName')}>
    <TextInput
      testId="contact-field-lastName"
      value={ctx.form.lastName ?? ''}
      onChange={(v) => ctx.setField({ lastName: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('lastName')}
    />
  </Field>
);

const positionRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('position')}>
    <TextInput
      value={ctx.form.position ?? ''}
      onChange={(v) => ctx.setField({ position: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('position')}
    />
  </Field>
);

const departmentRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('department')}>
    <TextInput
      value={ctx.form.department ?? ''}
      onChange={(v) => ctx.setField({ department: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('department')}
    />
  </Field>
);

const emailRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('email')}>
    <TextInput
      type="email"
      value={ctx.form.email ?? ''}
      onChange={(v) => ctx.setField({ email: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('email')}
    />
  </Field>
);

const phoneRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('phone')}>
    <TextInput
      value={ctx.form.phone ?? ''}
      onChange={(v) => ctx.setField({ phone: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('phone')}
    />
  </Field>
);

const mobileRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('mobile')}>
    <TextInput
      value={ctx.form.mobile ?? ''}
      onChange={(v) => ctx.setField({ mobile: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('mobile')}
    />
  </Field>
);

const preferredChannelRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name}>
    <PreferredChannelSelect ctx={ctx} />
  </Field>
);

const languageRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name}>
    <LanguageSelect ctx={ctx} />
  </Field>
);

/** Native date input — prostsze niż kontrahenta `gdprConsentAt` (bez masażu ISO-datetime). */
const birthdayRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('birthday')}>
    <TextInput
      type="date"
      value={ctx.form.birthday ?? ''}
      onChange={(v) => ctx.setField({ birthday: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('birthday')}
    />
  </Field>
);

const linkedinUrlRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name} error={ctx.getError?.('linkedinUrl')}>
    <TextInput
      type="url"
      value={ctx.form.linkedinUrl ?? ''}
      onChange={(v) => ctx.setField({ linkedinUrl: v || null })}
      disabled={!ctx.editable}
      invalid={ctx.hasError?.('linkedinUrl')}
    />
  </Field>
);

/** WRINKLE 1 — migrowany z gołego `<input type="checkbox">` na `<Checkbox>` (§4.12). */
const isPrimaryRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name}>
    <Checkbox
      checked={ctx.form.isPrimary}
      onChange={(e) => ctx.setField({ isPrimary: e.target.checked })}
      disabled={!ctx.editable}
      data-testid="contact-field-isPrimary"
    />
  </Field>
);

/** WRINKLE 1 — migrowany z gołego `<input type="checkbox">` na `<Checkbox>` (§4.12). */
const isDecisionMakerRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name}>
    <Checkbox
      checked={ctx.form.isDecisionMaker}
      onChange={(e) => ctx.setField({ isDecisionMaker: e.target.checked })}
      disabled={!ctx.editable}
      data-testid="contact-field-isDecisionMaker"
    />
  </Field>
);

/** WRINKLE 1 — migrowany z gołego `<textarea>` na `<Textarea>` (§4.12). */
const notesRenderer: FieldRenderer<ContactFormData> = (ctx) => (
  <Field label={ctx.field.name} colSpan={2} error={ctx.getError?.('notes')}>
    <Textarea
      value={ctx.form.notes ?? ''}
      onChange={(e) => ctx.setField({ notes: e.target.value || null })}
      disabled={!ctx.editable}
      rows={3}
      invalid={ctx.hasError?.('notes')}
      data-testid="contact-field-notes"
    />
  </Field>
);

const CONTACT_RENDERERS: Record<string, FieldRenderer<ContactFormData>> = {
  groups: groupsRenderer,
  category_id: categoryRenderer,
  title: titleRenderer,
  first_name: firstNameRenderer,
  last_name: lastNameRenderer,
  position: positionRenderer,
  department: departmentRenderer,
  email: emailRenderer,
  phone: phoneRenderer,
  mobile: mobileRenderer,
  preferred_channel: preferredChannelRenderer,
  language: languageRenderer,
  birthday: birthdayRenderer,
  linkedin_url: linkedinUrlRenderer,
  is_primary: isPrimaryRenderer,
  is_decision_maker: isDecisionMakerRenderer,
  notes: notesRenderer,
  // gdpr_consent_at — CELOWO brak wpisu (WRINKLE 2, patrz nagłówek pliku i pętla `fields` niżej).
};

const fields: Record<string, CoreFieldConfig<ContactFormData>> = {};
for (const slug of Object.keys(CONTACT_CORE_FIELD_SLUGS)) {
  const stateKey = CONTACT_CORE_FIELD_SLUGS[slug]!;
  if (slug === 'gdpr_consent_at') {
    // WRINKLE 2 — replikuj pre-existing gap: brak renderera, structuralNull żeby uniknąć
    // bursztynowego fallbacku silnika (patrz nagłówek pliku).
    fields[slug] = {
      stateKey,
      fieldType: CONTACT_FIELD_TYPES[slug] ?? 'date',
      structuralNull: true,
    };
    continue;
  }
  fields[slug] = {
    stateKey,
    fieldType: CONTACT_FIELD_TYPES[slug] ?? 'varchar',
    renderer: CONTACT_RENDERERS[slug]!,
    // Jedyne dwa pola z colSpan=2 w oryginalnych renderach (notes/groups rysują go zresztą
    // same na swoim <Field> — wpis tu jest kosmetyczny, gałąź natywna silnika jest martwa
    // dla tej kartoteki, patrz komentarz nagłówka pliku).
    ...(slug === 'notes' || slug === 'groups' ? { colSpan: 2 as const } : {}),
  };
}
// Trzeci gap (patrz nagłówek pliku) — `is_active` jest realnym core polem w katalogu, ale bez
// odpowiednika w `ContactFormData` (stąd brak `stateKey`) ani w `CONTACT_CORE_FIELD_SLUGS`
// (pętla wyżej go nie widzi). Dopisany RĘCZNIE żeby uniknąć bursztynowego fallbacku silnika.
fields.is_active = {
  fieldType: CONTACT_FIELD_TYPES.is_active ?? 'boolean',
  structuralNull: true,
};

export const contactEngineConfig: KartotekaEngineConfig<ContactFormData> = {
  moduleSlug: 'contractor_contacts',
  // LOAD-BEARING: engine's ext delegation emits `${testIdPrefix}-${slug}` — testid
  // `contact-ext-<slug>` (zachowanie 1:1 z przed-silnikowym ContactDataTab, patrz
  // `contact-ext-note`/`contact-ext-secret` w `ContactDataTab.test.tsx`).
  testIdPrefix: 'contact-ext',
  coreFieldSlugs: Object.keys(CONTACT_CORE_FIELD_SLUGS),
  fields,
  coerceEmptyToNull: false,
};
